import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { UserPlus, Eye, EyeOff } from "lucide-react";
import { getApiUrl, getAuthHeaders } from "../config/api";
import { useNotification } from "../contexts/NotificationContext";


// Signup Form Component
export default function Signup() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [selectedTier, setSelectedTier] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [expandedTiers, setExpandedTiers] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  // Suppress ResizeObserver loop error
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    fetchTiers();
  }, []);

  // Display order for plan cards: Free Trial first, then premium (Rodeo/Show)
  // tiers, then Horse Management and Fan View last. Any unlisted tier falls to the end.
  const TIER_DISPLAY_ORDER = [
    "free_trial",
    "showindividual",
    "showfamily",
    "showpremium",
    "horsemgmt",
    "fan_view",
  ];

  const fetchTiers = async () => {
    try {
      const headers = getAuthHeaders();

      const response = await axios.get(getApiUrl("/api/subscriptions/tiers"), {
        headers: headers
      });
      const orderIndex = (name) => {
        const i = TIER_DISPLAY_ORDER.indexOf(name);
        return i === -1 ? TIER_DISPLAY_ORDER.length : i;
      };
      const orderedTiers = [...response.data].sort(
        (a, b) => orderIndex(a.name) - orderIndex(b.name)
      );
      setTiers(orderedTiers);
      // Set default tier to the first card (Free Trial)
      if (orderedTiers.length > 0) {
        setSelectedTier(orderedTiers[0]);
      }
    } catch (err) {
      console.error("Error fetching tiers:", err);
      console.error("Response data:", err.response?.data);
      // Don't set error here as it might prevent the component from rendering
      // Just log the error and continue with empty tiers
    }
  };


  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    
    // Clear email error when user starts typing
    if (e.target.name === 'email' && emailError) {
      setEmailError("");
    }
  };

  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) return false;
    
    try {
      setIsCheckingEmail(true);
      const response = await axios.post(getApiUrl("/api/auth/check-email"), {
        email: email.toLowerCase()
      }, {
        headers: getAuthHeaders()
      });
      
      return response.data.exists;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailBlur = async () => {
    if (!formData.email) return;
    
    const exists = await checkEmailExists(formData.email);
    if (exists) {
      setEmailError("This email is already registered. Please use a different email or sign in instead.");
    }
  };

  const toggleTierExpansion = (tierId) => {
    setExpandedTiers(prev => ({
      ...prev,
      [tierId]: !prev[tierId]
    }));
  };

  const handleSignup = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      showError("Please fill in all fields");
      return;
    }

    if (!selectedTier) {
      showError("Please select a subscription plan");
      return;
    }

    if (emailError) {
      showError("Please fix the email error before continuing");
      return;
    }

    // Double-check email before proceeding
    const emailExists = await checkEmailExists(formData.email);
    if (emailExists) {
      setEmailError("This email is already registered. Please use a different email or sign in instead.");
      return;
    }

    setIsLoading(true);

    try {
      // Check if this is the free trial tier
      const isFreeTrial = selectedTier.monthly_price === 0 || selectedTier.name === 'free_trial';
      
      if (isFreeTrial) {
        // Direct signup for free trial (no payment required)
        const response = await axios.post(getApiUrl("/api/auth/signup"), {
          ...formData,
          tierId: selectedTier.id,
          // No paymentMethodId for free trial
        }, {
          headers: getAuthHeaders()
        });

        if (response.data.token) {
          // Store token and redirect to dashboard
          localStorage.setItem("token", response.data.token);
          showSuccess(`Welcome! Your 30-day free trial has started.`);
          navigate("/");
        } else {
          throw new Error("No authentication token received");
        }
      } else {
        // Create Stripe Checkout session for paid plans
        const response = await axios.post(getApiUrl("/api/auth/create-checkout-session"), {
          ...formData,
          tierId: selectedTier.id,
        }, {
          headers: getAuthHeaders()
        });

        if (response.data.url) {
          // Redirect to Stripe Checkout
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL received");
        }
      }
    } catch (err) {
      console.error("Signup error:", err);
      showError(err.response?.data?.error || err.message || "Signup failed. Please check your input.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* MyTrailer Logo */}
        <div className="text-center mb-8">
          <img 
            src="/MyTrailerLogo.png" 
            alt="MyTrailer Logo" 
            className="h-16 sm:h-20 mx-auto mb-6"
          />
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center">
            <UserPlus className="mr-2 text-blue-600" />
            Create Your MyTrailer Account
          </h1>
          <p className="mt-2 text-gray-600">Choose the plan that best fits your horse management needs</p>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <input
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      emailError 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    disabled={isCheckingEmail}
                  />
                  {isCheckingEmail && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                {emailError && (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Payment Information */}
              {selectedTier && (selectedTier.monthly_price === 0 || selectedTier.name === 'free_trial') ? (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Free Trial - No Payment Required
                        </h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p>Start your 30-day free trial immediately. No credit card required. You can upgrade anytime during or after your trial.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Secure Payment Processing
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>You'll be redirected to Stripe's secure checkout page to enter your payment information. Your card details are never stored on our servers.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Sign Up Button moved below Secure Payment Processing panel */}
              <div className="pt-4">
                <button
                  className={`w-full px-8 py-3 rounded-md font-medium text-white transition-colors ${
                    isLoading
                      ? "bg-gray-400 cursor-not-allowed"
                      : selectedTier && (selectedTier.monthly_price === 0 || selectedTier.name === 'free_trial')
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                  onClick={handleSignup}
                  disabled={isLoading}
                >
                  {isLoading 
                    ? (selectedTier && (selectedTier.monthly_price === 0 || selectedTier.name === 'free_trial') 
                        ? "Starting Free Trial..." 
                        : "Redirecting to Payment...")
                    : selectedTier && (selectedTier.monthly_price === 0 || selectedTier.name === 'free_trial')
                    ? "Start Free Trial - 30 Days"
                    : `Sign Up - ${selectedTier ? `$${selectedTier.monthly_price}/month` : "Select Plan"}`
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Subscription Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Choose Your Plan</h2>
            <div className="space-y-4">
              {tiers.length > 0 ? (
                tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className={`border-2 rounded-lg p-4 transition-colors ${
                      selectedTier?.id === tier.id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div 
                      className="cursor-pointer"
                      onClick={() => setSelectedTier(tier)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{tier.display_name}</h3>
                        <div className="text-right">
                          {tier.monthly_price === 0 || tier.name === 'free_trial' ? (
                            <span className="text-2xl font-bold text-green-600">Free</span>
                          ) : (
                            <>
                              <span className="text-2xl font-bold text-gray-900">${tier.monthly_price}</span>
                              <span className="text-gray-600">/month</span>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{tier.description}</p>
                      
                      <div className="space-y-2">
                        {/* Only show Additional Family Members if max_family_members > 0 */}
                        {tier.max_family_members > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Additional Family Members:</span>
                            <span className="font-medium">
                              {tier.max_family_members === -1 ? "Unlimited" : tier.max_family_members}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expandable Content Area */}
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTierExpansion(tier.id);
                        }}
                        className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
                      >
                        <span>View Details</span>
                        <svg
                          className={`w-4 h-4 transform transition-transform ${
                            expandedTiers[tier.id] ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {expandedTiers[tier.id] && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-600">
                            {/* Tier-specific content based on tier name */}
                            {tier.name?.toLowerCase().includes('free_trial') && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-2">30-Day Free Trial Features</h4>
                                <p className="mb-2">Try all features free for 30 days. No credit card required. Upgrade anytime during or after your trial.</p>
                                <ul className="list-disc list-inside space-y-1">
                                <li>Up to {tier.max_horses === -1 ? "unlimited" : tier.max_horses} horses</li>
                                <li>Horse information</li>
                                <li>Supplies management</li>
                                <li>Feed and supplements management</li>
                                <li>Vaccination tracking</li>
                                <li>Hoof care management</li>
                                <li>Dental care tracking</li>
                                <li>Breeding management</li>
                                <li>Deworming tracking</li>
                                <li>Farrier contacts</li>
                                <li>Veterinarian contacts</li>
                                <li>Connect with other users</li>
                                <li>Show Entries, Results and Memberships</li>
                                <li>KCIRodeo Show Entries, Results and Memberships Import</li>
                                <li>KCIRodeo Show Live Results</li>
                                <li>Analytics</li>
                                <li>Data export</li>
                                <li>Media - Capture your memories with Photos and Videos</li>
                                <li>Live Stream - Stream to your fans</li>
                                </ul>
                              </div>
                            )}

                            {tier.name?.toLowerCase().includes('horsemgmt') && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Horse Management Plan Features</h4>
                                <p className="mb-2">Perfect for individuals managing their horses, tracking feed, care and costs.</p>
                                <ul className="list-disc list-inside space-y-1">
                                <li>Up to 50 horses</li>
                                <li>Horse information</li>
                                <li>Supplies management</li>
                                <li>Feed and supplements management</li>
                                <li>Vaccination tracking</li>
                                <li>Hoof care management</li>
                                <li>Dental care tracking</li>
                                <li>Breeding management</li>
                                <li>Deworming tracking</li>
                                <li>Farrier contacts</li>
                                <li>Veterinarian contacts</li>
                                <li>Cost tracking</li>
                                </ul>
                              </div>
                            )}
                            
                            {tier.name?.toLowerCase().includes('showindividual') && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Show Individual Plan Features</h4>
                                <p className="mb-2">Horse management plus advanced tools for show competitors, tracking your entries, results and memberships.</p>
                                <ul className="list-disc list-inside space-y-1">
                                <li>Up to 4 horses</li>
                                <li>Horse information</li>
                                <li>Supplies management</li>
                                <li>Feed and supplements management</li>
                                <li>Vaccination tracking</li>
                                <li>Hoof care management</li>
                                <li>Dental care tracking</li>
                                <li>Breeding management</li>
                                <li>Deworming tracking</li>
                                <li>Farrier contacts</li>
                                <li>Veterinarian contacts</li>
                                <li>Connect with other users</li>
                                <li>Show Entries, Results and Memberships</li>
                                <li>KCIRodeo Show Entries, Results and Memberships Import</li>
                                <li>KCIRodeo Show Live Results</li>
                                <li>Analytics</li>
                                <li>Data export</li>
                                </ul>
                              </div>
                            )}
                            
                            {tier.name?.toLowerCase().includes('showfamily') && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Show Family Plan Features</h4>
                                <p className="mb-2">Everything in Show Individual Plan, plus family management capabilities tracking entries, results and memberships for each family member.</p>
                                <ul className="list-disc list-inside space-y-1">
                                <li>Up to 12 horses</li>
                                <li>Horse information</li>
                                <li>Supplies management</li>
                                <li>Feed and supplements management</li>
                                <li>Vaccination tracking</li>
                                <li>Hoof care management</li>
                                <li>Dental care tracking</li>
                                <li>Breeding management</li>
                                <li>Deworming tracking</li>
                                <li>Farrier contacts</li>
                                <li>Veterinarian contacts</li>
                                <li>Connect with other users</li>
                                <li>Show Entries, Results and Memberships</li>
                                <li>KCIRodeo Show Entries, Results and Memberships Import</li>
                                <li>KCIRodeo Show Live Results</li>                          
                                <li>Analytics</li>
                                <li>Data export</li>
                                <li>Family plan, up to {tier.max_family_members} additional family members</li>
                                </ul>
                              </div>
                            )}
                            
                            {tier.name?.toLowerCase().includes('showpremium') && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Show Premium Plan Features</h4>
                                <p className="mb-2">The ultimate package for Individuals, families, and groups.  Everything in Show Family Plan, plus capturing memories and advanced analytics.</p>
                                <ul className="list-disc list-inside space-y-1">
                                <li>Up to 20 horses</li>
                                <li>Horse information</li>
                                <li>Supplies management</li>
                                <li>Feed and supplements management</li>
                                <li>Vaccination tracking</li>
                                <li>Hoof care management</li>
                                <li>Dental care tracking</li>
                                <li>Breeding management</li>
                                <li>Deworming tracking</li>
                                <li>Farrier contacts</li>
                                <li>Veterinarian contacts</li>
                                <li>Connect with other users</li>
                                <li>Show Entries, Results and Memberships</li>
                                <li>KCIRodeo Show Entries, Results and Memberships Import</li>
                                <li>KCIRodeo Show Live Results</li>
                                <li>Advanced Analytics</li>
                                <li>Data export</li>
                                <li>Family plan Up to {tier.max_family_members} additional family members</li>
                                <li>Media - Capture your memories with Photos and Videos</li>
                                <li>Live Stream - Stream to your fans that are using Show Premium or Fan View</li>
                                <li>Insights - Get detailed insights and reports about your performance</li>
                                </ul>
                              </div>
                            )}
                            {tier.name?.toLowerCase().includes('fan_view') && (
                              <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Fan View Plan Features</h4>
                                <p className="mb-2">Watch live streams</p>
                                <ul className="list-disc list-inside space-y-1">
                                <li>Watch live streams from users that stream to you with Show Premium</li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading subscription options...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="mt-4 text-sm text-gray-600">
            Already have an account?{" "}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  navigate("/login");
                } catch (err) {
                  console.warn("Navigation error:", err);
                  window.location.href = "/login";
                }
              }}
              className="text-blue-600 hover:underline"
            >
              Sign in here
            </button>
          </p>
          
          <div className="mt-4 text-center">
            <a
              href="/privacy"
              className="text-xs text-gray-500 hover:text-blue-600 transition-colors duration-200"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

